import * as React from "react";
import { Helmet } from "react-helmet";
import { Block } from "kanopi-components/Block";
import { BrandLogo } from "kw/components/BrandLogo/BrandLogo";
import { Button } from "kanopi-components/Button";
import { CardBody } from "kanopi-components/Card";
import { DisplayMedium, ParagraphMedium } from "kanopi-components/typography";
import { Form } from "kanopi-components/Form";
import { FormControl, Input } from "kanopi-components/Input";
import { getSubdomain, handleErrorDisplay } from "kw/util/utils";
import { parseQueryString } from "kanopi-components/utils/browser";
import { PortalRow } from "kanopi-components/PortalRow";
import { useAuthenticate } from "../hooks/useAuthenticate";
import { useLoginForm } from "../hooks/useLoginForm";
import { useNavigate } from "@reach/router";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { Icon } from "kanopi-components/icons/Icon";
import { ParagraphSmall } from "baseui/typography";
import { useUser } from "kw/auth/useUser";
import { useReleaseHijack } from "kw/auth/hooks/useReleaseHijack";
import {
  getJwtFromCookies,
  startAuthRedirect,
  handleAuthError,
  callbackStateManager,
  clearAuthCookies,
} from "../utils/authUtils";
import { useOrganizationFeatureFlags } from "kw/auth/hooks/useOrganizationFeatureFlags";

export function LoginPage({
  initialValues = {},
  nextUrl,
}: {
  initialValues?: { email?: string; password?: string };
  nextUrl: string;
}) {
  const { releaseUser } = useReleaseHijack();
  const { flags } = useOrganizationFeatureFlags();
  const shouldShowAzureLoginButton = flags.enableAzureLogin;
  const shouldShowKanopiAuthenticatorButton = flags.enableKanopiAuthenticator;
  const navigate = useNavigate();
  const user = useUser();
  const { fieldProps, submit, loading: formLoading } = useLoginForm({ initialValues });
  const params = parseQueryString({ search: location?.search });
  const { authenticate: refreshUser, loading: refreshUserInProgress } = useAuthenticate(
    {
      login: true,
      now: false,
    }
  );

  const [css, theme] = useStyletron();

  const handleSubmit = async () => {
    try {
      await submit();
      navigate(params?.nextUrl || nextUrl, { replace: true });
    } catch (exc) {
      handleErrorDisplay(exc);
    }
  };

  const handleForgotPassword = () => {
    navigate("/password-reset/request");
  };

  /**
   * Handles authentication using Kanopi Authenticator
   * Checks for existing JWT in cookies first, then uses it if present
   * Otherwise initiates the redirect workflow to obtain a new token
   */
  const handleKanopiAuth = async () => {
    const jwt = getJwtFromCookies();

    if (jwt) {
      try {
        await refreshUser({ kanopiAuthenticatorJwt: jwt });
        navigate(String(params?.nextUrl || nextUrl), { replace: true });
        return;
      } catch (error) {
        clearAuthCookies();
        handleAuthError(error);
      }
    }

    startAuthRedirect(subdomain);
  };

  React.useEffect(() => {
    if (params?.hijacked) {
      refreshUser();
    }
  }, [params?.hijacked]);

  React.useEffect(() => {
    if (user?.isHijacked) {
      releaseUser();
    }
  }, []);

  /**
   * Handles the callback from authentication portal
   * Verifies the state parameter against stored value
   * Processes the JWT from cookies if present
   * Navigates to the next URL upon successful authentication
   */
  React.useEffect(() => {
    // Authentication callback handling
    if (params?.step === "callback") {
      const state = params?.state as string;
      if (!state) {
        handleErrorDisplay(
          { message: "Invalid state token" },
          "Authentication failed due to an invalid request. Try again."
        );
        return;
      }

      if (callbackStateManager.isProcessed(state)) {
        return;
      }

      callbackStateManager.markAsProcessed(state);

      if (!callbackStateManager.validateState(state)) {
        handleErrorDisplay(
          { message: "Invalid state token" },
          "Authentication failed due to an invalid request. Try again."
        );
        return;
      }

      const jwt = getJwtFromCookies();

      if (jwt) {
        refreshUser({ kanopiAuthenticatorJwt: jwt })
          .then(() => {
            navigate(String(params?.nextUrl || nextUrl), { replace: true });
          })
          .catch((error) => {
            clearAuthCookies();
            handleAuthError(error);
            console.log(error);
          });
      } else {
        handleErrorDisplay({ message: "Authentication failed" });
      }
    }
    // Trigger Kanopi Authenticator login flow from a remote application (e.g. auth portal)
    else if (params?.step === "kanopi-auth") {
      if (shouldShowKanopiAuthenticatorButton) {
        handleKanopiAuth();
      } else {
        handleErrorDisplay(
          {
            message: "Kanopi Authenticator is not enabled for your organization.",
          },
          "Kanopi login is not available for your organization"
        );
      }
    }
  }, [params?.step, params?.state]);

  const subdomain = getSubdomain();

  const hasAlternativeLogin =
    shouldShowAzureLoginButton || shouldShowKanopiAuthenticatorButton;

  return (
    <>
      <Helmet>
        <title>Log In{subdomain ? ` - ${subdomain}` : ""}</title>
      </Helmet>
      <PortalRow>
        <Block display="flex" justifyContent="center">
          <BrandLogo />
        </Block>
      </PortalRow>
      <Block
        backgroundColor={theme.colors.backgroundSecondary}
        borderRadius="roundedSurfaceRadius"
        maxWidth="400px"
        marginLeft={["scale700", "scale700", "auto"]}
        marginRight={["scale700", "scale700", "auto"]}
        marginTop="auto"
        marginBottom="auto"
      >
        <Form>
          <CardBody
            display="grid"
            gridGap="scale1200"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingTop="scale1200"
            paddingBottom="scale1200"
          >
            <Block>
              <DisplayMedium>Log in</DisplayMedium>
              <ParagraphMedium>
                Log in to your account to manage your insurance, make a claim and update
                your details.
              </ParagraphMedium>
              {params.error && params.description ? (
                <ParagraphSmall color={theme.colors.jumpRed400}>
                  {params.description}
                </ParagraphSmall>
              ) : null}
            </Block>

            <Block width="100%">
              <FormControl label="Your email address">
                <Input {...fieldProps.email} />
              </FormControl>
            </Block>
            <Block width="100%">
              <FormControl label="Your password">
                <Input type="password" {...fieldProps.password} />
              </FormControl>
            </Block>
            <Block display="grid" gridGap="scale800">
              <Button
                type="submit"
                kind="primary"
                wide
                onClick={handleSubmit}
                isLoading={user?.isHijacked || formLoading || refreshUserInProgress}
              >
                Login
              </Button>

              {hasAlternativeLogin && (
                <div
                  className={css({
                    position: "relative",
                    width: "100%",
                    textAlign: "center",
                  })}
                >
                  <div
                    className={css({
                      width: "100%",
                      position: "absolute",
                      height: "1px",
                      backgroundColor: theme.colors.borderOpaque,
                      display: "block",
                      top: "50%",
                      left: 0,
                      transform: "translateY(-50%)",
                    })}
                  />
                  <ParagraphSmall
                    overrides={{
                      Block: {
                        style: {
                          padding: "0 16px",
                          backgroundColor: theme.colors.backgroundSecondary,
                          position: "relative",
                          zIndex: 1,
                          display: "inline",
                        },
                      },
                    }}
                  >
                    or
                  </ParagraphSmall>
                </div>
              )}

              {shouldShowKanopiAuthenticatorButton && subdomain && (
                <Button
                  type="button"
                  kind={Button.KIND.secondary}
                  wide
                  startEnhancer={<Icon icon="key" />}
                  onClick={handleKanopiAuth}
                >
                  Login with Kanopi Authenticator
                </Button>
              )}

              {shouldShowAzureLoginButton ? (
                <Button
                  type="submit"
                  kind={Button.KIND.secondary}
                  wide
                  startEnhancer={<Icon icon="microsoftAzureLogo" />}
                  href="/user-sso/login?type=SamlAuthenticator"
                >
                  Sign in with Azure
                </Button>
              ) : null}

              <Button
                type="button"
                kind={Button.KIND.tertiary}
                size={Button.SIZE.mini}
                onClick={handleForgotPassword}
              >
                Forgot your password?
              </Button>
            </Block>
          </CardBody>
        </Form>
      </Block>
    </>
  );
}
